import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  GroupShortForm,
  Language,
  languageSchema,
  languageSchemaOptional,
  Menu,
  onQueryError,
  onQuerySuccess,
  TagShortForm,
  TaxGroup,
  UpdateMenu,
  useMenu,
} from '@/core';

const schema = z.object({
  name: languageSchema,
  description: languageSchemaOptional,
  base_price: z.string().nonempty({ message: t('GLOBAL.PRICE_REQUIRED') }),
  isActive: z.boolean(),
  groups: z.array(z.custom<GroupShortForm>()),
  tags: z.array(z.custom<TagShortForm>()),
  tax_group: z.object({
    id: z.number(),
    value: z.string(),
  }),
  price: z.number().nonnegative(),
});

type EditMenuFormValues = {
  name: Language;
  description: Language;
  base_price: string;
  isActive: boolean;
  image_id?: number;
  groups: GroupShortForm[];
  tags: TagShortForm[];
  tax_group: TaxGroup;
  price: number;
};

const mapEditMenuToApiForm = (editMenu: EditMenuFormValues): UpdateMenu => {
  const { name, description, base_price, isActive, groups, tags, tax_group } = editMenu;
  return {
    name,
    description,
    base_price,
    status: isActive ? 'active' : 'hidden',
    tax_id: tax_group.id,
    group_ids: groups.map((g) => g.id),
    tag_ids: tags.map((t) => t.id),
  };
};

const getInitialValues = (initialMenu?: Menu): EditMenuFormValues => {
  if (!initialMenu) {
    return {
      name: { en: '', cs: '', de: '' },
      description: { en: '', cs: '', de: '' },
      base_price: '0',
      isActive: true,
      image_id: undefined,
      groups: [],
      tags: [],
      tax_group: { id: 0, value: '21' },
      price: 0,
    };
  }
  return {
    name: initialMenu.name,
    description: initialMenu.description || { cs: '', en: '', de: '' },
    base_price: initialMenu.base_price,
    isActive: initialMenu.status === 'active',
    image_id: initialMenu.image_id,
    groups: initialMenu.groups || [],
    tags: initialMenu.tags || [],
    tax_group: initialMenu.tax_group || { id: 0, value: '21' },
    price: Number(initialMenu.final_price) || Number(initialMenu.base_price) || 0,
  };
};

export const useMenuForm = (initialMenu?: Menu) => {
  const queryClient = useQueryClient();
  const methods = useForm<EditMenuFormValues>({
    defaultValues: getInitialValues(initialMenu),
    mode: 'onChange',
    resolver: zodResolver(schema),
  });

  const {
    mutations: { updateMutation },
  } = useMenu();

  const { id } = useParams<{ id: string }>();
  const menuId = Number(id);

  const onSubmit = async (values: EditMenuFormValues) => {
    if (!id || isNaN(menuId)) return;
    const data = mapEditMenuToApiForm(values);
    const updateResponse = await updateMutation.mutateAsync({ id: menuId, data });
    if (updateResponse.status === 200 && updateResponse.data) {
      onQuerySuccess({
        queryKeysToInvalidate: [['menu', menuId]],
        successMessage: t('GLOBAL.SAVED'),
        queryClient,
      });
    } else {
      onQueryError({ errorMessage: t('MENU.SAVE_FAILURE') });
    }
  };

  return { methods, onSubmit, isPending: updateMutation.isPending };
};
